import React, { useContext, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import {
  map,
  addIndex,
  update,
  sortBy,
  prop,
  pipe,
  last,
  set,
  append,
  lensPath,
  lensProp,
  remove,
} from "ramda";
import { notBlank, notEmpty } from "../../../util/ext";
import { formatContributorName } from "../../../util/stringFormat";

import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/EditOutlined";
import ContributorDialog from "./ContributorDialog";
import { MediaContext } from "../../common/media-context";
import ColumnHeader from "./ColumnHeader";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    padding: 0,
    color: theme.palette.primary.darker,
  },

  button: {
    padding: 8,
  },

  header: {
    fontSize: "2rem",
    fontWeight: 700,
  },

  contributorsContainer: {
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
    borderBottomColor: "rgba(0, 0, 0, 0.42)",
  },

  contributorsContainerError: {
    borderBottomColor: theme.palette.error.main,
  },

  contributorsError: {
    color: theme.palette.error.main,
    marginBottom: theme.spacing(1),
  },

  contributorName: {
    lineHeight: "1.8rem",
  },
}));

const EditDescriptionInfoColumn = props => {
  const classes = useStyles();
  const context = useContext(MediaContext);
  const [dialog, setDialog] = useState({
    visible: false,
    index: null,
    contributor: {
      contributorType: "PERSON",
    },
  });

  const handleEditContributor = (index, contributor) => () => {
    console.log("Edit Contributor!");
    console.log(`index : ${index}`);
    setDialog({
      visible: true,
      title: "Edit Contributor",
      index: index,
      contributor: contributor,
    });
  };

  const handleRemoveContributor = index => () => {
    console.log("Remove Contributor!");
    context.setMedia(prev => {
      const newList = remove(index, 1, prev.contributors);
      return set(lensPath(["contributors"]), newList, prev);
    });
  };

  const handleAddContributor = () => {
    console.log("Add Contributor!");
    setDialog({
      visible: true,
      title: "Add a New Contributor",
      index: null,
      contributor: {
        contributorType: "PERSON",
      },
    });
  };

  const handleContributorCancel = () => {
    console.log("Cancel Contributor!");
    setDialog({
      visible: false,
      index: null,
      contributor: {},
    });
  };

  const handleContributorSave = (contrib, index) => {
    console.log("Save Contributor!");
    console.log(`Index : ${index}`);

    context.setMedia(prev => {
      if (notBlank(index)) {
        const newList = update(index, contrib, prev.contributors);
        return set(lensPath(["contributors"]), newList, prev);
      } else {
        const lastDisplayOrder = pipe(
          sortBy(prop("displayOrder")),
          last,
          prop("displayOrder"),
        )(prev.contributors);
        const newList = append(
          set(lensProp("displayOrder"), lastDisplayOrder + 1, contrib),
          prev.contributors,
        );
        return set(lensPath(["contributors"]), newList, prev);
      }
    });

    setDialog({
      visible: false,
      index: null,
      contributor: {},
    });
  };

  const createContributorRow = (onEditContributor, onRemoveContributor) => (
    contributor,
    index,
  ) => (
    <Grid
      container
      justifyContent="space-between"
      alignItems="baseline"
      key={`details-contributor-${index}`}
    >
      <Grid item className={classes.contributorName} xs>
        {formatContributorName(contributor)}
      </Grid>

      <IconButton
        style={{ padding: 9 }}
        aria-label="edit"
        onClick={onEditContributor(index, contributor)}
        size="large"
      >
        <EditIcon style={{ fontSize: "2rem" }} />
      </IconButton>
      <IconButton
        style={{ padding: 9 }}
        aria-label="delete"
        onClick={onRemoveContributor(index)}
        size="large"
      >
        <DeleteIcon style={{ fontSize: "2rem" }} />
      </IconButton>
    </Grid>
  );

  return (
    <div className={classes.root}>
      <ColumnHeader
        buttonText="Add Contributor"
        header="Contributors"
        onClick={handleAddContributor}
      />

      {notBlank(context.validator.hasError(context.errors, "contributors")) && (
        <div className={classes.contributorsError}>
          {context.validator.firstError(context.errors, "contributors")}
        </div>
      )}

      {notEmpty(context.media) && (
        <div className={classes.form}>
          <Grid spacing={2} container alignItems="flex-end">
            <Grid item xs>
              <div className={classes.contributorsContainer}>
                {addIndex(map)(
                  createContributorRow(
                    handleEditContributor,
                    handleRemoveContributor,
                  ),
                  context.media.contributors,
                )}
              </div>
            </Grid>
          </Grid>
        </div>
      )}

      <ContributorDialog
        visible={dialog.visible}
        title={dialog.title}
        contributor={dialog.contributor}
        index={dialog.index}
        onCancel={handleContributorCancel}
        onSave={handleContributorSave}
      />
    </div>
  );
};

export default EditDescriptionInfoColumn;
